import { IconX } from '@tabler/icons-react';
import { KeyboardEvent, ReactNode, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import InputField from '../InputField/InputField';

interface TagsFieldProps {
    value: string[];
    onAdd: (tag: string) => void;
    onRemove: (tag: string) => void;
    label?: ReactNode;
    description?: string;
    placeholder?: string;
    maxTags?: number;
    disabled?: boolean;
}

export default function TagsField({
    value,
    onAdd,
    onRemove,
    label,
    description,
    placeholder,
    maxTags = 5,
    disabled,
}: TagsFieldProps) {
    const { t } = useTranslation('common');
    const inputId = useId();
    const [input, setInput] = useState('');
    const [error, setError] = useState<string | null>(null);

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key !== 'Enter') return;
        e.preventDefault();
        if (disabled) return;

        const tag = input.trim();
        if (!tag) return;

        if (value.length >= maxTags) {
            setError(t('tagsField.maxTags', { count: maxTags }));
            return;
        }

        const duplicate = value.some((existing) => existing.toLowerCase() === tag.toLowerCase());
        if (duplicate) {
            setError(t('tagsField.duplicate'));
            return;
        }

        setError(null);
        setInput('');
        onAdd(tag);
    };

    const handleChange = (next: string) => {
        setInput(next);
        if (error) setError(null);
    };

    return (
        <div className="w-full flex flex-col gap-2">
            {label && (
                <label htmlFor={inputId} className="text-sm font-medium text-fg">
                    {label}
                </label>
            )}
            {value.length > 0 && (
                <div className="w-full flex flex-wrap items-center gap-2">
                    {value.map((tag) => (
                        <div
                            key={tag}
                            className="px-3 py-1 rounded-md bg-surface-2 border border-line flex items-center justify-between gap-2 text-sm text-fg"
                        >
                            <span className="min-w-0 truncate">{tag}</span>
                            <button
                                type="button"
                                aria-label={t('tagsField.remove', { tag })}
                                title={t('tagsField.remove', { tag })}
                                disabled={disabled}
                                onClick={() => onRemove(tag)}
                                className="inline-flex shrink-0 cursor-pointer items-center justify-center rounded-md border border-line px-1 py-1 text-fg/68 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <IconX size={14} />
                            </button>
                        </div>
                    ))}
                </div>
            )}
            <InputField
                id={inputId}
                value={input}
                onChange={(e) => handleChange(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
                descriptionBottom={description}
                error={error}
                disabled={disabled}
            />
        </div>
    );
}
